'use client'

import {Label, TableHead, TableHeader, TableRow} from '~/design-system/foundations'
import {tv} from 'tailwind-variants'
import React, {useEffect} from 'react'
import {HgButton} from '../HgButton'
import {HgTableTitleCell} from './HgTableTitleCell'
import HgDropdown from '../HgDropdown'
import {
  type ComparisonDropdownProps,
  type HgTableTitleComparisonRowProps,
} from './types'
import {useAnimateInContext} from '~/design-system/hg/Animation/AnimateIn/AnimateInContext'

const titleComparisonRowVariants = tv({
  slots: {
    tableRow:
      'grid grid-flow-col grid-cols-2 gap-s4 border-t-0 md:h-72 md:max-h-72 md:min-h-72 md:grid-cols-[repeat(auto-fit,minmax(156px,1fr))] md:grid-rows-1 md:gap-x-s5 md:pb-0',
    rowLabelCell: 'hidden w-full items-center justify-start md:flex',
    comparatorCell: 'hidden w-full items-center justify-center md:flex',
    dropdownCell: 'w-full items-center justify-center md:hidden',
  },
  variants: {
    twoUp: {
      true: {
        rowLabelCell: 'col-span-2',
        comparatorCell: 'flex',
        dropdownCell: 'hidden',
      },
      false: {
        tableRow: 'pb-s4',
      },
    },
    hasCollapsibleSections: {
      true: {
        tableRow: 'grid-rows-[auto_auto]',
        rowLabelCell: 'col-span-2 flex',
      },
    },
  },
  compoundVariants: [
    {
      twoUp: true,
      hasCollapsibleSections: false,
      class: {
        tableRow: 'h-72 max-h-72 min-h-72 ',
      },
    },
    {
      twoUp: true,
      hasCollapsibleSections: true,
      class: {
        tableRow: 'pb-s4',
      },
    },
    {
      twoUp: false,
      hasCollapsibleSections: true,
      class: {
        rowLabelCell: 'md:col-span-1',
      },
    },
  ],
})

const ComparisonDropdowns = ({
  selectedComparisons,
  onSelectionChange,
  comparisonOptions,
  primaryDropdownLabel,
  secondaryDropdownLabel,
}: ComparisonDropdownProps) => {
  const {dropdownCell} = titleComparisonRowVariants({
    twoUp: comparisonOptions.length === 2,
  })
  const handlePrimaryComparisonChange = (comparisonValue: string) => {
    onSelectionChange({
      primaryComparison: comparisonValue,
      secondaryComparison:
        comparisonValue !== selectedComparisons.secondaryComparison
          ? selectedComparisons.secondaryComparison
          : '',
    })
  }

  const handleSecondaryComparisonChange = (comparisonValue: string) => {
    onSelectionChange({
      ...selectedComparisons,
      secondaryComparison: comparisonValue === 'none' ? '' : comparisonValue,
    })
  }

  const secondaryOptions = [
    {value: 'none', label: '---'},
    ...comparisonOptions.filter(
      option => option.value !== selectedComparisons.primaryComparison
    ),
  ]

  return (
    <>
      <TableHead className={dropdownCell()}>
        <Label className="text-text-subdued arcadia-body-3">
          {primaryDropdownLabel || 'Plan type'}
          <HgDropdown
            classNames={{trigger: 'mt-s1 w-full justify-between'}}
            value={selectedComparisons.primaryComparison}
            onChange={handlePrimaryComparisonChange}
            options={comparisonOptions}
            position="popper"
          />
        </Label>
      </TableHead>
      <TableHead className={dropdownCell()}>
        <Label className="text-text-subdued arcadia-body-3">
          {secondaryDropdownLabel || 'Plan type'}
          <HgDropdown
            classNames={{trigger: 'mt-s1 w-full justify-between'}}
            value={selectedComparisons.secondaryComparison}
            onChange={handleSecondaryComparisonChange}
            options={secondaryOptions}
            position="popper"
          />
        </Label>
      </TableHead>
    </>
  )
}

export const HgTableTitleComparisonRow = ({
  rowLabel,
  comparatorLabels,
  hasCollapsibleSections,
  onToggleAllSections,
  areAllSectionsOpen,
  ...comparisonDropdownProps
}: HgTableTitleComparisonRowProps) => {
  const headerRef = React.useRef<HTMLTableSectionElement>(null)
  const {
    hasAnimationEnded: hasFadeInAnimationEnded,
    disableAnimation: isFadeInAnimationDisabled,
  } = useAnimateInContext()
  const {tableRow, rowLabelCell, comparatorCell} = titleComparisonRowVariants({
    twoUp: comparatorLabels.length === 2,
    hasCollapsibleSections,
  })

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) =>
        // Show bottom border only when scrolled
        entry.target.classList.toggle('border-b', entry.intersectionRatio < 1),
      {threshold: [1]}
    )

    if (headerRef.current) {
      observer.observe(headerRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !headerRef.current) return

    const updateHeight = () => {
      if (!headerRef.current) return
      // Account for the "animate in" effect translation of 50px
      const additionalOffset =
        hasFadeInAnimationEnded || isFadeInAnimationDisabled ? -2 : 48
      window.document?.documentElement.style.setProperty(
        // use for scroll margin top on table body sections
        '--comparison-table-header-height',
        `${headerRef.current.clientHeight + additionalOffset}px`
      )
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [hasFadeInAnimationEnded, isFadeInAnimationDisabled])

  return (
    <TableHeader
      ref={headerRef}
      className="sticky -top-[1px] z-navbar-z-index border-solid border-border-subdued bg-background-default pt-navbar-height"
    >
      <TableRow className={tableRow()}>
        <TableHead className={rowLabelCell()}>
          {hasCollapsibleSections ? (
            <HgButton
              className="w-full sm:w-fit"
              size="small"
              variant="tonal"
              onClick={onToggleAllSections}
            >
              {areAllSectionsOpen ? 'Close' : 'Expand'} All
            </HgButton>
          ) : (
            <p className="text-text-subdued arcadia-body-3">
              {rowLabel || 'Compare'}
            </p>
          )}
        </TableHead>
        {comparatorLabels.map(
          (label, index) =>
            label && (
              <HgTableTitleCell
                className={comparatorCell()}
                key={index}
                alignment="center"
                type="text"
                title={
                  <>
                    {label.defaultText.trim()}
                    {label?.primaryText && (
                      <span className="text-text-primary">
                        {` ${label?.primaryText?.trim()}`}
                      </span>
                    )}
                  </>
                }
              />
            )
        )}
        <ComparisonDropdowns {...comparisonDropdownProps} />
      </TableRow>
    </TableHeader>
  )
}
